/**
 * Answer form (moved from the workbench's task-detail side panel by 26c, brief 26 §6.4). Two modes:
 * - **questions** (unchanged): one field per clarification question (a select when the question has options) and a
 *   "Send answers" button, enabled once every question has an answer;
 * - **schema** (added by 26c): the fields of a served answer JSON Schema rendered with the schema form
 *   (`SchemaFields`); without a schema, `DEFAULT_ANSWER_SCHEMA` (one required textarea `answer`, 1–4000 characters).
 *   A rejected submit whose error carries `details.issues` (400 `validation_failed`) marks those fields inline;
 *   `fieldErrors` sets them from outside. Nothing is shown for other errors: the caller toasts them.
 */
import type { JsonSchemaObject } from '@agentic/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId, useMemo, useState } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Field, Input } from './fields.tsx';
import { Button } from './primitives.tsx';
import {
  defaultsFromSchema,
  fieldsFromSchema,
  type SchemaField,
  SchemaFields,
  type SchemaValues,
  toInputs,
  valuesFromObject,
  zodFromFields,
} from './schema-form.tsx';

/** A clarification question (questions mode). */
export interface AnswerQuestion {
  id: string;
  text: string;
  options?: string[];
}

/** Largest default answer (`OBLIGATION_ANSWER_TEXT_MAX` of `@agentic/contracts`). */
const DEFAULT_ANSWER_MAX = 4000;

/** The `answer` property of the default answer schema (a textarea, 1–4000 characters). */
export const DEFAULT_ANSWER_FIELD = {
  type: 'string',
  title: 'Answer',
  minLength: 1,
  maxLength: DEFAULT_ANSWER_MAX,
  'x-ui': { widget: 'textarea' },
} as const satisfies Record<string, unknown>;

/** The default answer schema (absent `answerSchemaRef`): one required text field `answer`, 1–4000 characters. */
export const DEFAULT_ANSWER_SCHEMA: JsonSchemaObject = {
  type: 'object',
  properties: { answer: DEFAULT_ANSWER_FIELD },
  required: ['answer'],
};

/**
 * The schema-mode form's own words (field messages of a rejected answer, the submit button, the form's name). English
 * defaults; an app with a dictionary passes its entries (`words`), every one optional.
 */
export interface AnswerFormWords {
  /** Accessible name of the form. */
  label: string;
  submit: string;
  required: string;
  atLeast: (min: number) => string;
  tooLong: string;
  atMost: (max: number) => string;
  invalid: string;
  check: string;
}

export const DEFAULT_ANSWER_FORM_WORDS: AnswerFormWords = {
  label: 'Answer',
  submit: 'Send answer',
  required: 'Required',
  atLeast: (min) => `At least ${min} characters`,
  tooLong: 'Too long',
  atMost: (max) => `At most ${max} characters`,
  invalid: 'Invalid value',
  check: 'Check this value',
};

interface CommonProps {
  /** Field id prefix, unique per form on the page. */
  idPrefix?: string;
}

/** Questions mode (clarification answers), the original props. */
export interface QuestionAnswerFormProps extends CommonProps {
  questions: readonly AnswerQuestion[];
  onSubmit: (answers: Record<string, string>) => Promise<unknown>;
  schema?: never;
}

/** Schema mode (obligation answers). */
export interface SchemaAnswerFormProps extends CommonProps {
  questions?: undefined;
  /** The served `answerSchema`; absent = `DEFAULT_ANSWER_SCHEMA`. */
  schema?: JsonSchemaObject | undefined;
  /** Label of the single default field (the question being answered); ignored with a served schema. */
  question?: string;
  /** Called with the answer object; reject to keep the form (field issues are marked inline). */
  onSubmit: (answer: Record<string, unknown>) => Promise<unknown>;
  /** Initial answer (Change answer starts from the answer that counts). */
  initial?: Record<string, unknown>;
  /** Field errors set by the caller (`fieldName → message`). */
  fieldErrors?: Readonly<Record<string, string>>;
  /** Submit button text (wins over `words.submit`). */
  submitLabel?: string;
  /** Accessible name of the form (default "Answer"; wins over `words.label`). */
  label?: string;
  /** The form's own words (`DEFAULT_ANSWER_FORM_WORDS` for any left out). */
  words?: Partial<AnswerFormWords>;
}

export type AnswerFormProps = QuestionAnswerFormProps | SchemaAnswerFormProps;

/** Answer form: clarification questions, or an answer schema (see the module comment). */
export function AnswerForm(props: AnswerFormProps) {
  return props.questions !== undefined ? <QuestionsForm {...props} /> : <SchemaAnswerForm {...props} />;
}

function QuestionsForm({ questions, onSubmit, idPrefix = 'clarify' }: QuestionAnswerFormProps) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const missing = questions.some((q) => !answers[q.id]?.trim());
  return (
    <form
      noValidate
      className="flex flex-col gap-3"
      aria-label="Clarification"
      onSubmit={(e) => {
        e.preventDefault();
        if (missing || pending) return;
        setPending(true);
        onSubmit(answers)
          .catch(() => undefined)
          .finally(() => setPending(false));
      }}
    >
      {questions.map((q) => {
        const id = `${idPrefix}-${q.id}`;
        return (
          <Field key={q.id} id={id} label={q.text} required>
            {q.options ? (
              <select
                id={id}
                className="h-[36px] w-full rounded-xs border border-control-border bg-surface px-2.5 text-[15px] focus-visible:border-focus"
                value={answers[q.id] ?? ''}
                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
              >
                <option value="">Choose…</option>
                {q.options.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            ) : (
              <Input
                id={id}
                value={answers[q.id] ?? ''}
                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
              />
            )}
          </Field>
        );
      })}
      <Button type="submit" variant="primary" disabled={missing} loading={pending} className="self-start">
        Send answers
      </Button>
    </form>
  );
}

interface Issue {
  path?: unknown;
  message?: unknown;
  code?: unknown;
  minimum?: unknown;
  maximum?: unknown;
  input?: unknown;
}

/** A user-facing message for one server issue (zod issue codes; the server's own text is never shown). */
function issueMessage(issue: Issue, words: AnswerFormWords): string {
  const min = typeof issue.minimum === 'number' ? issue.minimum : undefined;
  const max = typeof issue.maximum === 'number' ? issue.maximum : undefined;
  switch (issue.code) {
    case 'too_small':
      return min === undefined || min <= 1 ? words.required : words.atLeast(min);
    case 'too_big':
      return max === undefined ? words.tooLong : words.atMost(max);
    case 'invalid_type':
      return issue.input === undefined ? words.required : words.invalid;
    default:
      return words.check;
  }
}

/**
 * Field errors of a rejected answer: `details.issues` of a 400 `validation_failed` (zod issues whose `path` is
 * relative to the answer object) mapped to schema field names (`contact.email` → `contact__email`). Issues without
 * a field go under `''`. `undefined` when the error carries no issues.
 */
export function answerIssueErrors(
  error: unknown,
  fields: readonly SchemaField[],
  words: AnswerFormWords = DEFAULT_ANSWER_FORM_WORDS,
): Record<string, string> | undefined {
  if (typeof error !== 'object' || error === null || !('details' in error)) return undefined;
  const { details } = error;
  if (typeof details !== 'object' || details === null || !('issues' in details)) return undefined;
  const { issues } = details;
  if (!Array.isArray(issues)) return undefined;
  const out: Record<string, string> = {};
  const list: unknown[] = issues;
  for (const item of list) {
    const raw: Issue = typeof item === 'object' && item !== null ? item : {};
    const path = Array.isArray(raw.path) ? raw.path.map(String) : [];
    const field = fields.find((f) => (f.path ?? [f.name]).join('.') === path.join('.'));
    const key = field?.name ?? '';
    out[key] ??= issueMessage(raw, words);
  }
  return out;
}

function SchemaAnswerForm({
  schema,
  question,
  onSubmit,
  initial,
  fieldErrors,
  submitLabel,
  label,
  words: wordsIn,
  idPrefix,
}: SchemaAnswerFormProps) {
  const uid = useId();
  const words: AnswerFormWords = { ...DEFAULT_ANSWER_FORM_WORDS, ...wordsIn };
  const effective = useMemo<JsonSchemaObject>(() => {
    if (schema) return schema;
    if (!question) return DEFAULT_ANSWER_SCHEMA;
    return {
      ...DEFAULT_ANSWER_SCHEMA,
      properties: { answer: { ...DEFAULT_ANSWER_FIELD, title: question } },
    };
  }, [schema, question]);
  const fields = useMemo(() => fieldsFromSchema(effective), [effective]);
  const validator = useMemo(() => z.object({ v: zodFromFields(fields) }), [fields]);
  const form = useForm<{ v: SchemaValues }>({
    // The resolver's input type (zod's parsed shape of `v`) is wider than `SchemaValues`; the fields are built from the
    // same `fields`, so the values match (as in `SchemaForm`).
    resolver: zodResolver(validator) as unknown as Resolver<{ v: SchemaValues }>,
    defaultValues: { v: initial ? valuesFromObject(fields, initial) : defaultsFromSchema(fields) },
  });
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const applyErrors = (errors: Readonly<Record<string, string>>) => {
    let general: string | undefined;
    for (const [name, message] of Object.entries(errors)) {
      if (name === '' || !fields.some((f) => f.name === name)) general ??= message;
      else form.setError(`v.${name}` as `v.${string}`, { type: 'server', message });
    }
    setFormError(general);
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-apply only when the caller's errors change
  useEffect(() => {
    if (fieldErrors) applyErrors(fieldErrors);
  }, [fieldErrors]);
  const submit = form.handleSubmit(async ({ v }) => {
    setFormError(undefined);
    try {
      await onSubmit(toInputs(fields, v).inputs);
    } catch (error) {
      const errors = answerIssueErrors(error, fields, words);
      if (errors) applyErrors(errors);
    }
  });
  return (
    <form
      noValidate
      aria-label={label ?? words.label}
      className="flex flex-col gap-3"
      data-testid="answer-form"
      onSubmit={(e) => void submit(e)}
    >
      <SchemaFields
        fields={fields}
        prefix="v"
        idPrefix={idPrefix ?? `af${uid.replace(/[^a-zA-Z0-9]/g, '')}`}
        // `SchemaFields` is typed over `Record<string, unknown>` form values; these are `{ v: SchemaValues }` and every
        // field name it registers is `v.<name>` (the `prefix`), so the narrower register / control / errors fit.
        register={form.register as never}
        control={form.control as never}
        errors={form.formState.errors.v as never}
      />
      {formError && (
        <p className="text-[13px] text-bad-ink" role="alert" data-testid="answer-form-error">
          {formError}
        </p>
      )}
      <Button
        type="submit"
        variant="primary"
        loading={form.formState.isSubmitting}
        className="w-full sm:w-auto sm:self-start"
      >
        {submitLabel ?? words.submit}
      </Button>
    </form>
  );
}
